import React from "react";
import EChart from "../../../../components/Charts/Chart";
import { getToken, useTheme } from "../../../../utils/themeTokens";

const buildLineSeriesOption = (metricRows, modelType, title, yKeys) => {
  const hasData = Array.isArray(metricRows) && metricRows.length > 0;
  if (!hasData) return null;

  const epochs = metricRows.map((row, idx) => (row.epoch !== undefined ? row.epoch : idx + 1));
  const series = yKeys
    .map(({ key, name }) => {
      const values = metricRows.map((row) => (typeof row[key] === "number" ? row[key] : null));
      const anyValue = values.some((v) => v !== null);
      if (!anyValue) return null;
      return { name, type: "line", smooth: true, showSymbol: false, data: values };
    })
    .filter(Boolean);
  if (!series.length) return null;

  const colorPalette =
    modelType === "classification"
      ? [getToken("--chart-2", "#3b82f6"), getToken("--chart-1", "#10b981"), getToken("--chart-3", "#6366f1")]
      : [getToken("--chart-4", "#f97316"), getToken("--chart-1", "#22c55e"), getToken("--chart-3", "#6366f1")];
  const textColor = getToken("--foreground", "#111827");
  const mutedColor = getToken("--muted-foreground", "#64748b");
  const lineColor = getToken("--border-strong", "#e2e8f0");
  const splitColor = getToken("--border", "#f1f5f9");
  const axisLabelStyle = { fontSize: 12, color: mutedColor, fontWeight: 500 };

  return {
    backgroundColor: "transparent",
    title: { text: title, left: "center", textStyle: { fontSize: 15, fontWeight: 600, color: textColor } },
    tooltip: {
      trigger: "axis",
      backgroundColor: getToken("--popover", "#ffffff"),
      borderColor: lineColor,
      borderWidth: 1,
      textStyle: { fontSize: 12, color: getToken("--popover-foreground", "#0f172a") },
      confine: true,
    },
    legend: {
      top: 28,
      textStyle: { fontSize: 12, fontWeight: 500, color: mutedColor },
      itemWidth: 22,
      itemHeight: 14,
      itemGap: 18,
    },
    grid: { left: "5%", right: "4%", top: "18%", bottom: "16%", containLabel: true },
    xAxis: {
      type: "category",
      data: epochs,
      axisLabel: axisLabelStyle,
      axisLine: { lineStyle: { color: lineColor } },
      axisTick: { lineStyle: { color: lineColor } },
    },
    yAxis: {
      type: "value",
      axisLabel: axisLabelStyle,
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: { lineStyle: { color: splitColor } },
    },
    color: colorPalette,
    series,
  };
};

const LOSS_KEYS_CLS = [
  { key: "train/loss", name: "Train loss" },
  { key: "val/loss", name: "Val loss" },
];
const LOSS_KEYS_DET = [
  { key: "train/box_loss", name: "Train box" },
  { key: "val/box_loss", name: "Val box" },
];
const QUALITY_KEYS_CLS = [
  { key: "metrics/accuracy_top1", name: "Top-1 acc" },
  { key: "metrics/accuracy_top5", name: "Top-5 acc" },
];
const QUALITY_KEYS_DET = [
  { key: "metrics/mAP50(B)", name: "mAP@50" },
  { key: "metrics/precision(B)", name: "Precision" },
  { key: "metrics/recall(B)", name: "Recall" },
];

const ChartCard = ({ title, metricRows, modelType, yKeys, chartTitle, emptyLabel, metricsLoading }) => {
  useTheme(); // rebuild chart options with fresh token colors on theme switch
  const option = buildLineSeriesOption(metricRows, modelType, chartTitle, yKeys);

  return (
    <div className="training-chart-card">
      <div className="training-chart-card__header">
        <strong>{title}</strong>
        {metricsLoading && <span className="training-chart-card__loading">Loading…</span>}
      </div>
      <div className="training-chart-card__body">
        {option ? (
          <EChart option={option} />
        ) : (
          <div className="training-chart-card__empty">{emptyLabel}</div>
        )}
      </div>
    </div>
  );
};

const TrainingChartsSection = ({ metricRows, modelType, metricsLoading }) => (
  <section className="training-charts">
    <div className="training-charts__grid">
      <ChartCard
        title="Loss vs Epoch"
        metricRows={metricRows}
        modelType={modelType}
        yKeys={modelType === "classification" ? LOSS_KEYS_CLS : LOSS_KEYS_DET}
        chartTitle="Loss"
        emptyLabel="No epoch-wise loss metrics available for this run."
        metricsLoading={metricsLoading}
      />
      <ChartCard
        title="Quality vs Epoch"
        metricRows={metricRows}
        modelType={modelType}
        yKeys={modelType === "classification" ? QUALITY_KEYS_CLS : QUALITY_KEYS_DET}
        chartTitle="Quality"
        emptyLabel="No epoch-wise quality metrics available for this run."
        metricsLoading={metricsLoading}
      />
    </div>
  </section>
);

export default TrainingChartsSection;
